import React from "react";
import api, { MEDIA, API } from "../../lib/api";
import { toast } from "sonner";
import {
    Plus, Trash2, Search, TrendingUp, TrendingDown, DollarSign,
    FileText, BookOpen, BarChart2, X, ChevronDown, ChevronUp,
    Package as PkgIcon, RefreshCw, Download, ArrowUpRight, ArrowDownRight,
    ClipboardList,
} from "lucide-react";

// Helper: build authenticated PDF download URL and trigger download
function downloadPdf(path, filename) {
    const token = localStorage.getItem("st_token");
    fetch(`${API}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
        .then(r => {
            if (!r.ok) throw new Error("Eroare la generare PDF");
            return r.blob();
        })
        .then(blob => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
        })
        .catch(() => toast.error("Eroare la export PDF"));
}

const MONTH_LABELS = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const TYPE_COLORS = { income: "text-emerald-600", expense: "text-destructive" };
const TYPE_BG = { income: "bg-emerald-50 border-emerald-200", expense: "bg-red-50 border-red-200" };

const PM_LABELS = {
    cash: "Numerar", bank: "Bancă", card: "Card",
    cec: "CEC/BO", altul: "Alt",
};
const DOC_LABELS = {
    factura: "Factură", chitanta: "Chitanță", bon_fiscal: "Bon fiscal",
    op: "Ordin plată", extras_cont: "Extras cont", dispozitie_plata: "Disp. plată", altul: "Alt doc",
};

// ─────────────────────────────────────────────────────────────────────────────
export default function AdminAccounting() {
    const [tab, setTab] = React.useState("register");
    const [cats, setCats] = React.useState({ income: [], expense: [], payment_methods: [], doc_types: [] });

    React.useEffect(() => {
        api.get("/admin/accounting/categories").then(r => setCats(r.data || {})).catch(() => {});
    }, []);

    const TABS = [
        { id: "register",  label: "Registru",          icon: BookOpen },
        { id: "cashbook",  label: "Registrul de casă",  icon: DollarSign },
        { id: "nir",       label: "NIR",                icon: ClipboardList },
        { id: "reports",   label: "Rapoarte",           icon: BarChart2 },
    ];

    return (
        <div data-testid="admin-accounting">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-4">Contabilitate primară</h1>

            <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap
                            ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        <t.icon className="h-4 w-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "register"  && <RegisterTab cats={cats} />}
            {tab === "cashbook"  && <CashbookTab />}
            {tab === "nir"       && <NirTab cats={cats} />}
            {tab === "reports"   && <ReportsTab cats={cats} />}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Registru intrări/ieșiri
// ─────────────────────────────────────────────────────────────────────────────
function RegisterTab({ cats }) {
    const [entries, setEntries] = React.useState([]);
    const [total, setTotal]   = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [showForm, setShowForm] = React.useState(null); // "income"|"expense"|null
    const [editEntry, setEditEntry] = React.useState(null);

    // Filters
    const [fType, setFType]   = React.useState("");
    const [fCat,  setFCat]    = React.useState("");
    const [fPm,   setFPm]     = React.useState("");
    const [fFrom, setFFrom]   = React.useState("");
    const [fTo,   setFTo]     = React.useState("");
    const [fQ,    setFQ]      = React.useState("");

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            if (fType) p.set("type", fType);
            if (fCat)  p.set("category", fCat);
            if (fPm)   p.set("payment_method", fPm);
            if (fFrom) p.set("date_from", fFrom);
            if (fTo)   p.set("date_to", fTo);
            if (fQ)    p.set("q", fQ);
            const r = await api.get("/admin/accounting/entries?" + p.toString());
            setEntries(r.data.entries || []);
            setTotal(r.data.total || 0);
        } catch { toast.error("Eroare la încărcare"); }
        setLoading(false);
    }, [fType, fCat, fPm, fFrom, fTo, fQ]);

    React.useEffect(() => { load(); }, [load]);

    const del = async (id) => {
        if (!window.confirm("Ștergi înregistrarea?")) return;
        await api.delete(`/admin/accounting/entries/${id}`);
        toast.success("Șters"); load();
    };

    const sumIncome  = entries.filter(e => e.type === "income").reduce((s, e) => s + e.amount, 0);
    const sumExpense = entries.filter(e => e.type === "expense").reduce((s, e) => s + e.amount, 0);

    const allCats = [...(cats.income || []), ...(cats.expense || [])];

    return (
        <div className="space-y-4">
            {/* Quick action buttons */}
            <div className="flex flex-wrap gap-2 items-center">
                <button onClick={() => { setEditEntry(null); setShowForm("income"); }}
                    className="btn-primary flex items-center gap-2">
                    <ArrowUpRight className="h-4 w-4" /> Adaugă intrare
                </button>
                <button onClick={() => { setEditEntry(null); setShowForm("expense"); }}
                    className="flex items-center gap-2 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm font-medium hover:opacity-90">
                    <ArrowDownRight className="h-4 w-4" /> Adaugă ieșire
                </button>
                <button
                    onClick={() => {
                        const p = new URLSearchParams();
                        if (fType) p.set("type", fType);
                        if (fCat)  p.set("category", fCat);
                        if (fPm)   p.set("payment_method", fPm);
                        if (fFrom) p.set("date_from", fFrom);
                        if (fTo)   p.set("date_to", fTo);
                        const qs = p.toString() ? "?" + p.toString() : "";
                        downloadPdf(`/admin/accounting/pdf/registru${qs}`, `registru${fFrom ? "-" + fFrom : ""}.pdf`);
                    }}
                    className="btn-outline flex items-center gap-2 ml-auto">
                    <Download className="h-4 w-4" /> Export PDF
                </button>
            </div>

            {/* Filters */}
            <div className="card-soft p-3 flex flex-wrap gap-2 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                    <input value={fQ} onChange={e => setFQ(e.target.value)}
                        placeholder="Caută descriere, document..."
                        className="input-soft flex-1 !py-1.5 text-sm" />
                </div>
                <select value={fType} onChange={e => { setFType(e.target.value); setFCat(""); }}
                    className="input-soft !py-1.5 text-sm">
                    <option value="">Toate tipurile</option>
                    <option value="income">Intrări</option>
                    <option value="expense">Ieșiri</option>
                </select>
                <select value={fCat} onChange={e => setFCat(e.target.value)}
                    className="input-soft !py-1.5 text-sm">
                    <option value="">Toate categoriile</option>
                    {(fType === "income" ? cats.income : fType === "expense" ? cats.expense : allCats).map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                </select>
                <select value={fPm} onChange={e => setFPm(e.target.value)}
                    className="input-soft !py-1.5 text-sm">
                    <option value="">Toate metodele</option>
                    {(cats.payment_methods || []).map(pm => (
                        <option key={pm} value={pm}>{PM_LABELS[pm] || pm}</option>
                    ))}
                </select>
                <input type="date" value={fFrom} onChange={e => setFFrom(e.target.value)}
                    className="input-soft !py-1.5 text-sm" title="De la" />
                <input type="date" value={fTo} onChange={e => setFTo(e.target.value)}
                    className="input-soft !py-1.5 text-sm" title="Până la" />
                <button onClick={load} className="btn-ghost p-1.5" title="Reîncarcă">
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* Totals bar */}
            {entries.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                    <div className="card-soft p-3 flex items-center gap-3">
                        <ArrowUpRight className="h-5 w-5 text-emerald-600 shrink-0" />
                        <div>
                            <div className="text-xs text-muted-foreground">Intrări selectate</div>
                            <div className="font-mono font-bold text-emerald-600">{sumIncome.toFixed(2)} RON</div>
                        </div>
                    </div>
                    <div className="card-soft p-3 flex items-center gap-3">
                        <ArrowDownRight className="h-5 w-5 text-destructive shrink-0" />
                        <div>
                            <div className="text-xs text-muted-foreground">Ieșiri selectate</div>
                            <div className="font-mono font-bold text-destructive">{sumExpense.toFixed(2)} RON</div>
                        </div>
                    </div>
                    <div className="card-soft p-3 flex items-center gap-3">
                        <DollarSign className={`h-5 w-5 shrink-0 ${sumIncome - sumExpense >= 0 ? "text-emerald-600" : "text-destructive"}`} />
                        <div>
                            <div className="text-xs text-muted-foreground">Diferență</div>
                            <div className={`font-mono font-bold ${sumIncome - sumExpense >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                                {(sumIncome - sumExpense).toFixed(2)} RON
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Entries table */}
            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted">
                        <tr>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Data</th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Tip</th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Categorie</th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Descriere</th>
                            <th className="hidden md:table-cell text-left px-3 py-3 text-xs uppercase text-muted-foreground">Document</th>
                            <th className="hidden md:table-cell text-left px-3 py-3 text-xs uppercase text-muted-foreground">Metodă</th>
                            <th className="text-right px-3 py-3 text-xs uppercase text-muted-foreground">Sumă</th>
                            <th className="px-3 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 && (
                            <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">
                                {loading ? "Se încarcă..." : "Nicio înregistrare."}
                            </td></tr>
                        )}
                        {entries.map(e => {
                            const catLabel = [...(cats.income || []), ...(cats.expense || [])].find(c => c.value === e.category)?.label || e.category;
                            return (
                                <tr key={e.entry_id} className="border-t border-border hover:bg-muted/30">
                                    <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border ${TYPE_BG[e.type]}`}>
                                            {e.type === "income"
                                                ? <ArrowUpRight className="h-3 w-3" />
                                                : <ArrowDownRight className="h-3 w-3" />}
                                            {e.type === "income" ? "Intrare" : "Ieșire"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{catLabel}</td>
                                    <td className="px-3 py-2 max-w-[200px] truncate" title={e.description}>{e.description || "—"}</td>
                                    <td className="hidden md:table-cell px-3 py-2 text-xs text-muted-foreground">
                                        {e.doc_type && DOC_LABELS[e.doc_type] ? DOC_LABELS[e.doc_type] : ""}
                                        {e.document_number && <span className="ml-1 font-mono">#{e.document_number}</span>}
                                    </td>
                                    <td className="hidden md:table-cell px-3 py-2 text-xs">
                                        <span className="px-1.5 py-0.5 rounded bg-muted font-mono">{PM_LABELS[e.payment_method] || e.payment_method}</span>
                                    </td>
                                    <td className={`px-3 py-2 text-right font-mono font-bold ${TYPE_COLORS[e.type]}`}>
                                        {e.type === "expense" ? "−" : "+"}{e.amount.toFixed(2)}
                                        <span className="text-xs font-normal text-muted-foreground ml-1">{e.currency}</span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => { setEditEntry(e); setShowForm(e.type); }}
                                                className="btn-ghost p-1 text-muted-foreground hover:text-foreground text-xs">
                                                Edit
                                            </button>
                                            <button onClick={() => del(e.entry_id)}
                                                className="btn-ghost p-1 text-destructive">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {total > entries.length && (
                    <div className="p-3 text-center text-xs text-muted-foreground">
                        Se afișează {entries.length} din {total} înregistrări
                    </div>
                )}
            </div>

            {/* Add/Edit form modal */}
            {showForm && (
                <EntryFormModal
                    type={showForm}
                    entry={editEntry}
                    cats={cats}
                    onClose={() => { setShowForm(null); setEditEntry(null); }}
                    onSaved={() => { setShowForm(null); setEditEntry(null); load(); }}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry form modal (shared for income + expense + edit)
// ─────────────────────────────────────────────────────────────────────────────
function EntryFormModal({ type, entry, cats, onClose, onSaved }) {
    const isEdit = !!entry;
    const catList = type === "income" ? (cats.income || []) : (cats.expense || []);
    const defaultCat = catList[0]?.value || "";

    const [form, setForm] = React.useState({
        date:            entry?.date || new Date().toISOString().slice(0, 10),
        category:        entry?.category || defaultCat,
        description:     entry?.description || "",
        amount:          entry?.amount || "",
        payment_method:  entry?.payment_method || "cash",
        doc_type:        entry?.doc_type || "factura",
        document_number: entry?.document_number || "",
        notes:           entry?.notes || "",
    });
    const [saving, setSaving] = React.useState(false);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.amount || Number(form.amount) <= 0) return toast.error("Suma trebuie să fie pozitivă");
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/admin/accounting/entries/${entry.entry_id}`, form);
                toast.success("Actualizat");
            } else {
                await api.post("/admin/accounting/entries", { ...form, type });
                toast.success(type === "income" ? "Intrare adăugată" : "Ieșire adăugată");
            }
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare");
        }
        setSaving(false);
    };

    const title = isEdit ? "Editează înregistrare"
        : type === "income" ? "Adaugă intrare" : "Adaugă ieșire";
    const accent = type === "income" ? "border-emerald-400" : "border-destructive";

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className={`bg-card rounded-2xl w-full max-w-lg border-t-4 ${accent}`} onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <h3 className="font-heading text-lg flex items-center gap-2">
                        {type === "income"
                            ? <ArrowUpRight className="h-5 w-5 text-emerald-600" />
                            : <ArrowDownRight className="h-5 w-5 text-destructive" />}
                        {title}
                    </h3>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Data</label>
                            <input type="date" value={form.date} onChange={e => set("date", e.target.value)}
                                className="input-soft w-full !py-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Sumă (RON) *</label>
                            <input type="number" step="0.01" min="0" value={form.amount}
                                onChange={e => set("amount", e.target.value)}
                                placeholder="0.00" className="input-soft w-full !py-2 text-sm font-mono" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Categorie</label>
                        <select value={form.category} onChange={e => set("category", e.target.value)}
                            className="input-soft w-full !py-2 text-sm">
                            {catList.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Descriere</label>
                        <input value={form.description} onChange={e => set("description", e.target.value)}
                            placeholder="Ex: Factură furnizor nr. 1234..." className="input-soft w-full !py-2 text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Metodă plată</label>
                            <select value={form.payment_method} onChange={e => set("payment_method", e.target.value)}
                                className="input-soft w-full !py-2 text-sm">
                                {(cats.payment_methods || []).map(pm => (
                                    <option key={pm} value={pm}>{PM_LABELS[pm] || pm}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Tip document</label>
                            <select value={form.doc_type} onChange={e => set("doc_type", e.target.value)}
                                className="input-soft w-full !py-2 text-sm">
                                {(cats.doc_types || []).map(d => (
                                    <option key={d} value={d}>{DOC_LABELS[d] || d}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Număr document</label>
                        <input value={form.document_number} onChange={e => set("document_number", e.target.value)}
                            placeholder="Ex: 2024-001" className="input-soft w-full !py-2 text-sm font-mono" />
                    </div>
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Notițe (opțional)</label>
                        <textarea rows="2" value={form.notes} onChange={e => set("notes", e.target.value)}
                            className="input-soft w-full text-sm" />
                    </div>
                    <button onClick={save} disabled={saving}
                        className={`w-full !py-2.5 font-medium rounded-lg text-sm ${type === "income" ? "btn-primary" : "bg-destructive text-destructive-foreground hover:opacity-90"}`}>
                        {saving ? "Se salvează..." : isEdit ? "Salvează modificările" : title}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Registrul de casă
// ─────────────────────────────────────────────────────────────────────────────
function CashbookTab() {
    const [days,    setDays]    = React.useState([]);
    const [dateFrom, setDateFrom] = React.useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 29);
        return d.toISOString().slice(0, 10);
    });
    const [dateTo,  setDateTo]  = React.useState(new Date().toISOString().slice(0, 10));
    const [expanded, setExpanded] = React.useState({});

    const load = React.useCallback(async () => {
        try {
            const r = await api.get(`/admin/accounting/cash-register?date_from=${dateFrom}&date_to=${dateTo}`);
            setDays((r.data || []).reverse()); // most recent first
        } catch { toast.error("Eroare"); }
    }, [dateFrom, dateTo]);

    React.useEffect(() => { load(); }, [load]);

    const toggle = (date) => setExpanded(e => ({ ...e, [date]: !e[date] }));

    const finalBalance = days.length > 0 ? days[0].balance : 0;

    return (
        <div className="space-y-4">
            <div className="card-soft p-3 flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">De la:</label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                        className="input-soft !py-1.5 text-sm" />
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">Până la:</label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                        className="input-soft !py-1.5 text-sm" />
                </div>
                <button onClick={load} className="btn-ghost p-1.5"><RefreshCw className="h-4 w-4" /></button>
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Sold numerar perioadă:</span>
                    <span className={`font-mono font-bold text-lg ${finalBalance >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                        {finalBalance.toFixed(2)} RON
                    </span>
                </div>
            </div>

            <div className="card-soft overflow-hidden divide-y divide-border">
                {days.length === 0 && (
                    <div className="p-8 text-center text-muted-foreground">
                        <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-20" />
                        <p>Nicio înregistrare cash în această perioadă.</p>
                        <p className="text-xs mt-1">Adaugă înregistrări cu metoda de plată „Numerar" din tab-ul Registru.</p>
                    </div>
                )}
                {days.map(day => (
                    <div key={day.date}>
                        <button onClick={() => toggle(day.date)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 text-sm">
                            <span className="font-mono font-medium w-24 text-left">{day.date}</span>
                            <span className="text-emerald-600 font-mono">+{(day.income || 0).toFixed(2)}</span>
                            <span className="text-destructive font-mono">−{(day.expense || 0).toFixed(2)}</span>
                            <span className="ml-auto font-mono font-bold">
                                Sold: <span className={day.balance >= 0 ? "text-emerald-600" : "text-destructive"}>{day.balance.toFixed(2)}</span> RON
                            </span>
                            {expanded[day.date] ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                        </button>
                        {expanded[day.date] && day.entries.length > 0 && (
                            <div className="bg-muted/20 px-4 pb-3">
                                <table className="w-full text-xs">
                                    <tbody>
                                        {day.entries.map((e, i) => (
                                            <tr key={i} className="border-t border-border/40">
                                                <td className="py-1.5 text-muted-foreground font-mono">{e.entry_id}</td>
                                                <td className="py-1.5 px-2">{e.description || "—"}</td>
                                                <td className="py-1.5 text-right font-mono font-medium">{e.amount.toFixed(2)} RON</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — NIR
// ─────────────────────────────────────────────────────────────────────────────
function NirTab({ cats }) {
    const [nirs,      setNirs]     = React.useState([]);
    const [showForm,  setShowForm] = React.useState(false);
    const [viewing,   setViewing]  = React.useState(null);
    const [suppliers, setSuppliers]= React.useState([]);
    const [products,  setProducts] = React.useState([]);

    React.useEffect(() => {
        api.get("/admin/suppliers").then(r => setSuppliers(r.data || [])).catch(() => {});
        api.get("/admin/inventory/search").then(r => setProducts(r.data || [])).catch(() => {});
        loadNirs();
    }, []);

    const loadNirs = async () => {
        const r = await api.get("/admin/accounting/nir");
        setNirs(r.data || []);
    };

    const del = async (id) => {
        if (!window.confirm("Ștergi NIR-ul? Se vor șterge și intrările contabile generate automat.")) return;
        await api.delete(`/admin/accounting/nir/${id}`);
        toast.success("NIR șters"); loadNirs();
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                    Notă de Intrare-Recepție — documentează recepția mărfii de la furnizori și opțional actualizează stocul.
                </p>
                <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2 shrink-0">
                    <Plus className="h-4 w-4" /> NIR nou
                </button>
            </div>

            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted">
                        <tr>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Nr. NIR</th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Data</th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Furnizor</th>
                            <th className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">Factură furnizor</th>
                            <th className="text-right px-3 py-3 text-xs uppercase text-muted-foreground">Valoare totală</th>
                            <th className="text-center px-3 py-3 text-xs uppercase text-muted-foreground">Stoc actualizat</th>
                            <th className="px-3 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {nirs.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Niciun NIR înregistrat.</td></tr>
                        )}
                        {nirs.map(n => (
                            <tr key={n.nir_id} className="border-t border-border hover:bg-muted/30">
                                <td className="px-3 py-2 font-mono font-bold">#{n.nir_number}</td>
                                <td className="px-3 py-2 font-mono text-xs">{n.date}</td>
                                <td className="px-3 py-2">{n.supplier_name || "—"}</td>
                                <td className="px-3 py-2 font-mono text-xs">{n.invoice_number || "—"}</td>
                                <td className="px-3 py-2 text-right font-mono font-bold">{n.total.toFixed(2)} RON</td>
                                <td className="px-3 py-2 text-center">
                                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${n.update_stock ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                                        {n.update_stock ? "Da" : "Nu"}
                                    </span>
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => setViewing(n)}
                                            className="btn-ghost text-xs p-1 text-muted-foreground hover:text-foreground">
                                            Detalii
                                        </button>
                                        <button
                                            onClick={() => downloadPdf(`/admin/accounting/nir/${n.nir_id}/pdf`, `NIR-${n.nir_number}.pdf`)}
                                            className="btn-ghost p-1 text-primary" title="Descarcă PDF">
                                            <Download className="h-3.5 w-3.5" />
                                        </button>
                                        <button onClick={() => del(n.nir_id)}
                                            className="btn-ghost p-1 text-destructive">
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showForm && (
                <NirFormModal
                    suppliers={suppliers}
                    products={products}
                    cats={cats}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); loadNirs(); }}
                />
            )}
            {viewing && (
                <NirDetailModal nir={viewing} onClose={() => setViewing(null)} />
            )}
        </div>
    );
}

function NirFormModal({ suppliers, products, cats, onClose, onSaved }) {
    const [form, setForm] = React.useState({
        date:            new Date().toISOString().slice(0, 10),
        supplier_id:     "",
        supplier_name:   "",
        invoice_number:  "",
        payment_method:  "altul",
        notes:           "",
        update_stock:    true,
    });
    const [items, setItems] = React.useState([]);
    const [prodQ, setProdQ] = React.useState("");
    const [prodResults, setProdResults] = React.useState([]);
    const [saving, setSaving] = React.useState(false);
    const timerRef = React.useRef(null);

    // Product search
    React.useEffect(() => {
        clearTimeout(timerRef.current);
        if (!prodQ.trim()) { setProdResults([]); return; }
        timerRef.current = setTimeout(() => {
            const q = prodQ.toLowerCase();
            setProdResults(products.filter(p =>
                p.name?.toLowerCase().includes(q) || p.code?.toLowerCase().includes(q)
            ).slice(0, 10));
        }, 200);
    }, [prodQ, products]);

    const addItem = (prod) => {
        setProdQ(""); setProdResults([]);
        setItems(prev => [...prev, {
            product_id: prod.product_id,
            name: prod.name,
            code: prod.code || "",
            qty: 1,
            unit_price: prod.purchase_price || 0,
            vat_rate: 19,
        }]);
    };

    const addManualItem = () => {
        setItems(prev => [...prev, { product_id: null, name: "", code: "", qty: 1, unit_price: 0, vat_rate: 19 }]);
    };

    const updateItem = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    const subtotal  = items.reduce((s, it) => s + (it.qty * it.unit_price), 0);
    const vatTotal  = items.reduce((s, it) => s + (it.qty * it.unit_price * it.vat_rate / 100), 0);
    const total     = subtotal + vatTotal;

    const save = async () => {
        if (!items.length) return toast.error("Adaugă cel puțin un produs");
        setSaving(true);
        try {
            await api.post("/admin/accounting/nir", { ...form, items });
            toast.success("NIR creat");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
        setSaving(false);
    };

    const pickSupplier = (s) => {
        setForm(f => ({ ...f, supplier_id: s.supplier_id, supplier_name: s.name }));
    };

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <h3 className="font-heading text-lg flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-primary" /> NIR nou
                    </h3>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Header fields */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Data *</label>
                            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                                className="input-soft w-full !py-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Nr. factură furnizor</label>
                            <input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
                                placeholder="Ex: F-2024-001" className="input-soft w-full !py-2 text-sm font-mono" />
                        </div>
                    </div>

                    {/* Supplier */}
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Furnizor</label>
                        <div className="flex gap-2">
                            <select value={form.supplier_id}
                                onChange={e => {
                                    const s = suppliers.find(s => s.supplier_id === e.target.value);
                                    setForm(f => ({ ...f, supplier_id: e.target.value, supplier_name: s?.name || "" }));
                                }}
                                className="input-soft !py-2 text-sm flex-1">
                                <option value="">— Selectează furnizor —</option>
                                {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
                            </select>
                            <input value={form.supplier_name}
                                onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value, supplier_id: "" }))}
                                placeholder="sau scrie manual"
                                className="input-soft !py-2 text-sm flex-1" />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Metodă plată furnizor</label>
                            <select value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}
                                className="input-soft !py-2 text-sm w-full">
                                {(cats.payment_methods || []).map(pm => (
                                    <option key={pm} value={pm}>{PM_LABELS[pm] || pm}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-end pb-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={form.update_stock}
                                    onChange={e => setForm(f => ({ ...f, update_stock: e.target.checked }))} />
                                <span className="text-sm">Actualizează stocul produselor</span>
                            </label>
                        </div>
                    </div>

                    {/* Items */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Produse recepționate</label>
                            <button onClick={addManualItem} className="text-xs text-primary hover:underline flex items-center gap-1">
                                <Plus className="h-3 w-3" /> Adaugă linie manuală
                            </button>
                        </div>

                        {/* Product search */}
                        <div className="relative mb-2">
                            <input value={prodQ} onChange={e => setProdQ(e.target.value)}
                                placeholder="Caută produs din inventar..."
                                className="input-soft w-full text-sm" />
                            {prodResults.length > 0 && (
                                <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded shadow-lg max-h-40 overflow-y-auto">
                                    {prodResults.map(p => (
                                        <button key={p.product_id} onClick={() => addItem(p)}
                                            className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between gap-2">
                                            <span className="truncate">{p.name}
                                                {p.code && <span className="text-muted-foreground ml-1 text-xs">#{p.code}</span>}
                                            </span>
                                            <span className="text-xs text-muted-foreground shrink-0">stoc: {p.stock}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Items table */}
                        {items.length > 0 && (
                            <div className="border border-border rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="text-left px-2 py-2">Produs</th>
                                            <th className="text-left px-2 py-2 w-16">Cod</th>
                                            <th className="text-right px-2 py-2 w-16">Qty</th>
                                            <th className="text-right px-2 py-2 w-24">Preț unit.</th>
                                            <th className="text-right px-2 py-2 w-16">TVA %</th>
                                            <th className="text-right px-2 py-2 w-24">Total</th>
                                            <th className="w-8"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((it, idx) => (
                                            <tr key={idx} className="border-t border-border">
                                                <td className="px-2 py-1">
                                                    {it.product_id
                                                        ? <span className="font-medium">{it.name}</span>
                                                        : <input value={it.name} onChange={e => updateItem(idx, { name: e.target.value })}
                                                            placeholder="Denumire produs" className="input-soft !py-0.5 text-xs w-full" />
                                                    }
                                                </td>
                                                <td className="px-2 py-1">
                                                    {it.product_id
                                                        ? <span className="font-mono text-muted-foreground">{it.code}</span>
                                                        : <input value={it.code} onChange={e => updateItem(idx, { code: e.target.value })}
                                                            placeholder="Cod" className="input-soft !py-0.5 text-xs w-full font-mono" />
                                                    }
                                                </td>
                                                <td className="px-2 py-1">
                                                    <input type="number" min="1" value={it.qty}
                                                        onChange={e => updateItem(idx, { qty: Number(e.target.value) })}
                                                        className="input-soft !py-0.5 text-xs w-full text-right font-mono" />
                                                </td>
                                                <td className="px-2 py-1">
                                                    <input type="number" min="0" step="0.01" value={it.unit_price}
                                                        onChange={e => updateItem(idx, { unit_price: Number(e.target.value) })}
                                                        className="input-soft !py-0.5 text-xs w-full text-right font-mono" />
                                                </td>
                                                <td className="px-2 py-1">
                                                    <input type="number" min="0" step="1" value={it.vat_rate}
                                                        onChange={e => updateItem(idx, { vat_rate: Number(e.target.value) })}
                                                        className="input-soft !py-0.5 text-xs w-full text-right font-mono" />
                                                </td>
                                                <td className="px-2 py-1 text-right font-mono font-bold">
                                                    {(it.qty * it.unit_price * (1 + it.vat_rate / 100)).toFixed(2)}
                                                </td>
                                                <td className="px-2 py-1">
                                                    <button onClick={() => removeItem(idx)} className="text-destructive hover:opacity-70">
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-muted/50">
                                        <tr>
                                            <td colSpan={5} className="px-2 py-2 text-right text-xs font-medium">Subtotal (fără TVA):</td>
                                            <td className="px-2 py-2 text-right font-mono">{subtotal.toFixed(2)}</td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td colSpan={5} className="px-2 py-1 text-right text-xs font-medium">TVA:</td>
                                            <td className="px-2 py-1 text-right font-mono">{vatTotal.toFixed(2)}</td>
                                            <td></td>
                                        </tr>
                                        <tr className="border-t border-border">
                                            <td colSpan={5} className="px-2 py-2 text-right text-sm font-bold">TOTAL:</td>
                                            <td className="px-2 py-2 text-right font-mono font-bold text-base">{total.toFixed(2)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-wide text-muted-foreground mb-1">Observații</label>
                        <textarea rows="2" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                            className="input-soft w-full text-sm" />
                    </div>
                </div>

                <div className="p-5 border-t border-border flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                        {items.length} produse · Total: <span className="font-mono font-bold text-foreground">{total.toFixed(2)} RON</span>
                    </div>
                    <button onClick={save} disabled={saving} className="btn-primary !py-2.5 px-6">
                        {saving ? "Se salvează..." : "Salvează NIR"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function NirDetailModal({ nir, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <h3 className="font-heading text-lg">NIR #{nir.nir_number}</h3>
                        <p className="text-sm text-muted-foreground">{nir.date} · {nir.supplier_name}</p>
                    </div>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-muted-foreground">Furnizor:</span> <span className="font-medium">{nir.supplier_name || "—"}</span></div>
                        <div><span className="text-muted-foreground">Factură:</span> <span className="font-mono">{nir.invoice_number || "—"}</span></div>
                        <div><span className="text-muted-foreground">Stoc actualizat:</span> <span>{nir.update_stock ? "Da" : "Nu"}</span></div>
                        <div><span className="text-muted-foreground">Înregistrat de:</span> <span>{nir.created_by_name || "—"}</span></div>
                    </div>

                    <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                        <thead className="bg-muted">
                            <tr>
                                <th className="text-left px-3 py-2 text-xs">Produs</th>
                                <th className="text-right px-3 py-2 text-xs">Qty</th>
                                <th className="text-right px-3 py-2 text-xs">Preț unit.</th>
                                <th className="text-right px-3 py-2 text-xs">TVA</th>
                                <th className="text-right px-3 py-2 text-xs">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(nir.items || []).map((it, i) => (
                                <tr key={i} className="border-t border-border">
                                    <td className="px-3 py-2">{it.name}
                                        {it.code && <span className="text-xs text-muted-foreground ml-1">#{it.code}</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">{it.qty}</td>
                                    <td className="px-3 py-2 text-right font-mono">{it.unit_price?.toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{it.vat_rate}%</td>
                                    <td className="px-3 py-2 text-right font-mono font-bold">{it.line_total?.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-muted/50 text-sm">
                            <tr><td colSpan={4} className="px-3 py-1.5 text-right">Subtotal:</td><td className="px-3 py-1.5 text-right font-mono">{nir.subtotal?.toFixed(2)}</td></tr>
                            <tr><td colSpan={4} className="px-3 py-1 text-right text-muted-foreground">TVA:</td><td className="px-3 py-1 text-right font-mono text-muted-foreground">{nir.vat_total?.toFixed(2)}</td></tr>
                            <tr className="border-t border-border"><td colSpan={4} className="px-3 py-2 text-right font-bold">TOTAL:</td><td className="px-3 py-2 text-right font-mono font-bold">{nir.total?.toFixed(2)} RON</td></tr>
                        </tfoot>
                    </table>

                    {nir.notes && <p className="text-sm text-muted-foreground italic">{nir.notes}</p>}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB 4 — Rapoarte
// ─────────────────────────────────────────────────────────────────────────────

function toIso(d) { return d.toISOString().slice(0, 10); }

function getPresetRange(preset) {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    switch (preset) {
        case "azi":
            return { from: toIso(now), to: toIso(now), groupBy: "day", label: "Azi" };
        case "saptamana": {
            const dow = now.getDay() === 0 ? 6 : now.getDay() - 1; // Mon=0
            const mon = new Date(y, m, d - dow);
            const sun = new Date(y, m, d - dow + 6);
            return { from: toIso(mon), to: toIso(sun), groupBy: "day", label: "Săptămâna curentă" };
        }
        case "luna":
            return { from: toIso(new Date(y, m, 1)), to: toIso(new Date(y, m + 1, 0)), groupBy: "day", label: `${MONTH_LABELS[m]} ${y}` };
        case "an":
            return { from: `${y}-01-01`, to: `${y}-12-31`, groupBy: "month", label: `An ${y}` };
        case "luna_trecuta": {
            const pm = m === 0 ? 11 : m - 1;
            const py = m === 0 ? y - 1 : y;
            return { from: toIso(new Date(py, pm, 1)), to: toIso(new Date(py, pm + 1, 0)), groupBy: "day", label: `${MONTH_LABELS[pm]} ${py}` };
        }
        case "an_trecut":
            return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31`, groupBy: "month", label: `An ${y - 1}` };
        default:
            return { from: "", to: "", groupBy: "month", label: "Toate" };
    }
}

const PRESETS = [
    { id: "azi",          label: "Azi" },
    { id: "saptamana",    label: "Săptămână" },
    { id: "luna",         label: "Luna curentă" },
    { id: "luna_trecuta", label: "Luna trecută" },
    { id: "an",           label: "An curent" },
    { id: "an_trecut",    label: "An trecut" },
    { id: "custom",       label: "Perioadă" },
];

function ReportsTab({ cats }) {
    const [preset,   setPreset]   = React.useState("an");
    const [custom,   setCustom]   = React.useState({ from: "", to: "" });
    const [grouped,  setGrouped]  = React.useState([]);
    const [summary,  setSummary]  = React.useState(null);

    // Resolve active date range
    const range = React.useMemo(() => {
        if (preset === "custom") {
            const days = custom.from && custom.to
                ? (new Date(custom.to) - new Date(custom.from)) / 86400000
                : 400;
            return { from: custom.from, to: custom.to, groupBy: days <= 62 ? "day" : "month", label: custom.from ? `${custom.from} — ${custom.to}` : "Perioadă personalizată" };
        }
        return getPresetRange(preset);
    }, [preset, custom]);

    const load = React.useCallback(async () => {
        const p = new URLSearchParams();
        if (range.from) p.set("date_from", range.from);
        if (range.to)   p.set("date_to",   range.to);
        p.set("group_by", range.groupBy);
        const [g, s] = await Promise.all([
            api.get("/admin/accounting/grouped?" + p.toString()).then(r => r.data || []),
            api.get("/admin/accounting/summary?" + new URLSearchParams(
                Object.fromEntries(Array.from(p.entries()).filter(([k]) => k !== "group_by"))
            ).toString()).then(r => r.data),
        ]);
        setGrouped(g);
        setSummary(s);
    }, [range]);

    React.useEffect(() => { load(); }, [load]);

    const maxBar = Math.max(...grouped.map(g => Math.max(g.income, g.expense)), 1);

    const allLabels = {
        ...(cats.income  || []).reduce((o, c) => ({ ...o, [c.value]: c.label }), {}),
        ...(cats.expense || []).reduce((o, c) => ({ ...o, [c.value]: c.label }), {}),
    };

    // Format bar label based on groupBy
    const barLabel = (period) => {
        if (range.groupBy === "month") {
            const [y, m] = period.split("-");
            return MONTH_LABELS[parseInt(m) - 1] || period;
        }
        // day: show DD.MM or just day number
        const parts = period.split("-");
        return `${parts[2]}.${parts[1]}`;
    };

    const pdfFilename = `raport-${range.from || "toate"}-${range.to || ""}.pdf`;
    const pdfParams   = new URLSearchParams();
    if (range.from) pdfParams.set("date_from", range.from);
    if (range.to)   pdfParams.set("date_to",   range.to);

    return (
        <div className="space-y-5">

            {/* Period selector + PDF export */}
            <div className="card-soft p-3 flex flex-wrap gap-2 items-center">
                {PRESETS.map(p => (
                    <button key={p.id} onClick={() => setPreset(p.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                            ${preset === p.id
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted hover:bg-muted/70 text-foreground"}`}>
                        {p.label}
                    </button>
                ))}
                {preset === "custom" && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <input type="date" value={custom.from}
                            onChange={e => setCustom(c => ({ ...c, from: e.target.value }))}
                            className="input-soft !py-1.5 text-sm" />
                        <span className="text-muted-foreground text-sm">—</span>
                        <input type="date" value={custom.to}
                            onChange={e => setCustom(c => ({ ...c, to: e.target.value }))}
                            className="input-soft !py-1.5 text-sm" />
                    </div>
                )}
                <button
                    onClick={() => downloadPdf(
                        `/admin/accounting/pdf/registru?${pdfParams.toString()}`,
                        pdfFilename
                    )}
                    className="btn-outline flex items-center gap-2 ml-auto text-sm">
                    <Download className="h-4 w-4" /> Export PDF
                </button>
            </div>

            {/* Period label */}
            <div className="text-sm text-muted-foreground px-1">
                Perioadă: <span className="font-medium text-foreground">{range.label}</span>
                {range.from && <span className="ml-2 font-mono text-xs">{range.from} → {range.to}</span>}
            </div>

            {/* Summary totals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card-soft p-4 border-l-4 border-emerald-400">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total intrări</div>
                    <div className="font-mono font-bold text-2xl text-emerald-600">{(summary?.income_total || 0).toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">RON</div>
                </div>
                <div className="card-soft p-4 border-l-4 border-destructive">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Total ieșiri</div>
                    <div className="font-mono font-bold text-2xl text-destructive">{(summary?.expense_total || 0).toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">RON</div>
                </div>
                <div className={`card-soft p-4 border-l-4 ${(summary?.profit || 0) >= 0 ? "border-primary" : "border-destructive"}`}>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Profit brut</div>
                    <div className={`font-mono font-bold text-2xl ${(summary?.profit || 0) >= 0 ? "text-primary" : "text-destructive"}`}>
                        {(summary?.profit || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">RON</div>
                </div>
            </div>

            {/* Chart */}
            <div className="card-soft p-4">
                <h3 className="font-medium mb-4">
                    Grafic {range.groupBy === "day" ? "zilnic" : "lunar"} — {range.label}
                </h3>
                {grouped.length === 0 ? (
                    <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
                        Nicio înregistrare în această perioadă.
                    </div>
                ) : (
                    <>
                        <div className="flex items-end gap-0.5 h-36 overflow-x-auto pb-1">
                            {grouped.map((g) => (
                                <div key={g.period}
                                    className="flex flex-col items-center gap-0.5 group shrink-0"
                                    style={{ minWidth: grouped.length > 20 ? "18px" : grouped.length > 12 ? "28px" : "auto", flex: grouped.length <= 20 ? "1" : "none" }}>
                                    <div className="w-full relative flex flex-col justify-end" style={{ height: "112px" }}>
                                        <div className="w-full bg-emerald-200 rounded-t-sm group-hover:bg-emerald-400 transition-colors"
                                             style={{ height: `${(g.income / maxBar) * 108}px` }}
                                             title={`${g.period}: Intrări ${g.income.toFixed(2)} RON`} />
                                        <div className="absolute bottom-0 left-0 right-0 bg-red-300/70 rounded-t-sm group-hover:bg-red-400 transition-colors"
                                             style={{ height: `${(g.expense / maxBar) * 108}px` }}
                                             title={`${g.period}: Ieșiri ${g.expense.toFixed(2)} RON`} />
                                    </div>
                                    <span className="text-[9px] text-muted-foreground whitespace-nowrap">{barLabel(g.period)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-200 rounded-sm inline-block" /> Intrări</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-300 rounded-sm inline-block" /> Ieșiri</span>
                        </div>
                    </>
                )}
            </div>

            {/* By category */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CategoryBreakdown
                        title="Intrări pe categorii"
                        data={summary.by_category?.income || {}}
                        labels={allLabels}
                        color="emerald"
                        total={summary.income_total}
                    />
                    <CategoryBreakdown
                        title="Ieșiri pe categorii"
                        data={summary.by_category?.expense || {}}
                        labels={allLabels}
                        color="red"
                        total={summary.expense_total}
                    />
                </div>
            )}

            {/* Payment method breakdown */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PaymentBreakdown title="Intrări pe metodă plată" data={summary.by_payment?.income || {}} />
                    <PaymentBreakdown title="Ieșiri pe metodă plată" data={summary.by_payment?.expense || {}} />
                </div>
            )}
        </div>
    );
}

function CategoryBreakdown({ title, data, labels, color, total }) {
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]);
    const max = sorted[0]?.[1] || 1;
    const barColor = color === "emerald" ? "bg-emerald-400" : "bg-red-400";
    return (
        <div className="card-soft p-4">
            <h4 className="font-medium text-sm mb-3">{title}</h4>
            {sorted.length === 0
                ? <p className="text-sm text-muted-foreground">Nicio înregistrare.</p>
                : <div className="space-y-2">
                    {sorted.map(([cat, amt]) => (
                        <div key={cat}>
                            <div className="flex items-center justify-between text-xs mb-0.5">
                                <span>{labels[cat] || cat}</span>
                                <span className="font-mono">{amt.toFixed(2)} RON
                                    {total > 0 && <span className="text-muted-foreground ml-1">({Math.round(amt / total * 100)}%)</span>}
                                </span>
                            </div>
                            <div className="h-1.5 bg-muted rounded-full">
                                <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${(amt / max) * 100}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

function PaymentBreakdown({ title, data }) {
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]);
    return (
        <div className="card-soft p-4">
            <h4 className="font-medium text-sm mb-3">{title}</h4>
            {sorted.length === 0
                ? <p className="text-sm text-muted-foreground">Nicio înregistrare.</p>
                : <div className="space-y-1">
                    {sorted.map(([pm, amt]) => (
                        <div key={pm} className="flex items-center justify-between text-sm">
                            <span className="px-2 py-0.5 rounded bg-muted text-xs font-mono">{PM_LABELS[pm] || pm}</span>
                            <span className="font-mono font-medium">{amt.toFixed(2)} RON</span>
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}
